import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  HandCoins,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  User,
  FileText,
  MessageSquare,
  Save,
  X,
  CreditCard,
  Plus,
  Search,
  CheckCircle2,
  Building2,
  Folder,
} from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface CreatePromiseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (data: any) => void;
}

interface PromiseFormData {
  // Customer Information
  customerName: string;
  customerAccountNumber: string;
  customerEmail: string;
  customerPhone: string;
  
  // Promise Details
  promiseDate: Date | undefined;
  promisedAmount: string;
  confirmationMethod: string;
  caseId?: string;  // Optional link to collection case
  
  // Invoice Selection
  selectedInvoices: Set<string>;
  invoiceAllocations: Record<string, number>;
  
  // Notes
  notes: string;
  sendConfirmation: boolean;
}

interface MockInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: Date;
  originalAmount: number;
  balanceRemaining: number;
  daysOverdue: number;
}

// Mock customer data for demonstration
const mockCustomers = [
  {
    id: '1',
    name: 'Acme Corporation',
    accountNumber: 'ACC-001',
    email: 'accounts@acme.com',
    phone: '+1 (555) 123-4567',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    accountNumber: 'ACC-002',
    email: 'finance@globalind.com',
    phone: '+1 (555) 234-5678',
  },
  {
    id: '3',
    name: 'Tech Solutions Inc',
    accountNumber: 'ACC-003',
    email: 'billing@techsol.com',
    phone: '+1 (555) 345-6789',
  },
];

// Mock invoices for demonstration
const mockInvoices: MockInvoice[] = [
  {
    id: 'INV-001',
    invoiceNumber: 'INV-2024-001',
    invoiceDate: new Date(2024, 0, 15),
    originalAmount: 5000,
    balanceRemaining: 5000,
    daysOverdue: 45,
  },
  {
    id: 'INV-002',
    invoiceNumber: 'INV-2024-002',
    invoiceDate: new Date(2024, 1, 1),
    originalAmount: 3500,
    balanceRemaining: 3500,
    daysOverdue: 30,
  },
  {
    id: 'INV-003',
    invoiceNumber: 'INV-2024-003',
    invoiceDate: new Date(2024, 1, 15),
    originalAmount: 2000,
    balanceRemaining: 2000,
    daysOverdue: 15,
  },
];

// Mock collection cases for demonstration
const mockCases = [
  { id: 'case-1', caseNumber: 'AR-00001', status: 'Active', priority: 'High' },
  { id: 'case-2', caseNumber: 'AR-00002', status: 'Active', priority: 'Medium' },
  { id: 'case-3', caseNumber: 'AR-00003', status: 'Active', priority: 'Low' },
  { id: 'case-4', caseNumber: 'AR-00004', status: 'On Hold', priority: 'Medium' },
];

export const CreatePromiseDrawer: React.FC<CreatePromiseDrawerProps> = ({ open, onOpenChange, onSave }) => {
  const [activeTab, setActiveTab] = useState('customer');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<typeof mockCustomers[0] | null>(null);
  
  const [formData, setFormData] = useState<PromiseFormData>({
    customerName: '',
    customerAccountNumber: '',
    customerEmail: '',
    customerPhone: '',
    promiseDate: undefined,
    promisedAmount: '',
    confirmationMethod: '',
    selectedInvoices: new Set(),
    invoiceAllocations: {},
    notes: '',
    sendConfirmation: true,
  });

  // Reset form when drawer closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form
      setActiveTab('customer');
      setCustomerSearchQuery('');
      setSelectedCustomer(null);
      setFormData({
        customerName: '',
        customerAccountNumber: '',
        customerEmail: '',
        customerPhone: '',
        promiseDate: undefined,
        promisedAmount: '',
        confirmationMethod: '',
        selectedInvoices: new Set(),
        invoiceAllocations: {},
        notes: '',
        sendConfirmation: true,
      });
    }
    onOpenChange(newOpen);
  };

  // Handle customer selection
  const handleSelectCustomer = (customer: typeof mockCustomers[0]) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customerName: customer.name,
      customerAccountNumber: customer.accountNumber,
      customerEmail: customer.email,
      customerPhone: customer.phone,
    });
    // Use setTimeout to ensure state updates complete before switching tabs
    // This prevents the race condition where the tab is still disabled
    setTimeout(() => {
      setActiveTab('promise');
    }, 0);
  };

  // Handle invoice selection
  const handleToggleInvoice = (invoiceId: string, checked: boolean) => {
    const newSelected = new Set(formData.selectedInvoices);
    const newAllocations = { ...formData.invoiceAllocations };
    
    if (checked) {
      newSelected.add(invoiceId);
      const invoice = mockInvoices.find(inv => inv.id === invoiceId);
      if (invoice) {
        newAllocations[invoiceId] = invoice.balanceRemaining;
      }
    } else {
      newSelected.delete(invoiceId);
      delete newAllocations[invoiceId];
    }
    
    setFormData({
      ...formData,
      selectedInvoices: newSelected,
      invoiceAllocations: newAllocations,
    });
  };

  // Handle allocation amount change
  const handleAllocationChange = (invoiceId: string, amount: number) => {
    setFormData({
      ...formData,
      invoiceAllocations: {
        ...formData.invoiceAllocations,
        [invoiceId]: amount,
      },
    });
  };

  // Calculate total promised amount from allocations
  const calculateTotalFromAllocations = () => {
    return Object.values(formData.invoiceAllocations).reduce((sum, amount) => sum + amount, 0);
  };

  // Validation
  const validateForm = (): string | null => {
    if (!selectedCustomer) {
      return 'Please select a customer';
    }
    
    if (!formData.promiseDate) {
      return 'Please select a promise date';
    }
    
    if (!formData.promisedAmount || parseFloat(formData.promisedAmount) <= 0) {
      return 'Please enter a valid promised amount';
    }
    
    if (!formData.confirmationMethod) {
      return 'Please select a confirmation method';
    }
    
    if (formData.selectedInvoices.size === 0) {
      return 'Please select at least one invoice';
    }
    
    const totalAllocated = calculateTotalFromAllocations();
    const promisedAmount = parseFloat(formData.promisedAmount);
    
    if (Math.abs(totalAllocated - promisedAmount) > 0.01) {
      return `Invoice allocations ($${totalAllocated.toFixed(2)}) must equal promised amount ($${promisedAmount.toFixed(2)})`;
    }
    
    return null;
  };

  // Handle save
  const handleSave = () => {
    const validationError = validateForm();
    
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const data = {
      customer: selectedCustomer,
      promiseDate: formData.promiseDate,
      promisedAmount: parseFloat(formData.promisedAmount),
      confirmationMethod: formData.confirmationMethod,
      selectedInvoices: Array.from(formData.selectedInvoices),
      invoiceAllocations: formData.invoiceAllocations,
      notes: formData.notes,
      sendConfirmation: formData.sendConfirmation,
    };

    if (onSave) {
      onSave(data);
    }

    toast.success('Promise to pay created successfully');
    handleOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    handleOpenChange(false);
  };

  // Filter customers based on search
  const filteredCustomers = mockCustomers.filter(customer =>
    customer.name.toLowerCase().includes(customerSearchQuery.toLowerCase()) ||
    customer.accountNumber.toLowerCase().includes(customerSearchQuery.toLowerCase())
  );

  // Calculate totals
  const totalInvoiceAmount = Array.from(formData.selectedInvoices)
    .map(id => mockInvoices.find(inv => inv.id === id))
    .filter(Boolean)
    .reduce((sum, inv) => sum + (inv?.balanceRemaining || 0), 0);

  const totalAllocated = calculateTotalFromAllocations();

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="flex items-center gap-2 text-2xl">
                <HandCoins className="h-6 w-6 text-primary" />
                Create New Promise
              </SheetTitle>
              <SheetDescription className="mt-1">
                Record a new payment commitment from a customer
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Create Promise
              </Button>
            </div>
          </div>

          {/* Progress Indicator */}
          {selectedCustomer && (
            <div className="mt-4">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
                <User className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Customer:</span>
                <span className="font-medium text-primary">{selectedCustomer.name}</span>
                <span className="text-sm text-muted-foreground">({selectedCustomer.accountNumber})</span>
              </div>
            </div>
          )}
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="px-6 pt-4 border-b">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="customer" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Customer
                {selectedCustomer && (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="promise" 
                className="flex items-center gap-2"
                disabled={!selectedCustomer}
              >
                <FileText className="h-4 w-4" />
                Promise Details
                {formData.promiseDate && formData.promisedAmount && formData.confirmationMethod && (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="invoices" 
                className="flex items-center gap-2"
                disabled={!selectedCustomer}
              >
                <DollarSign className="h-4 w-4" />
                Invoices
                {formData.selectedInvoices.size > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                    {formData.selectedInvoices.size}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="notes" 
                className="flex items-center gap-2"
                disabled={!selectedCustomer}
              >
                <MessageSquare className="h-4 w-4" />
                Notes
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1">
            {/* Customer Tab */}
            <TabsContent value="customer" className="px-6 py-4 space-y-6 mt-0 overflow-visible">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Select Customer
                  </CardTitle>
                  <CardDescription>Search and select the customer making the promise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by customer name or account number..."
                      value={customerSearchQuery}
                      onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Customer List */}
                  <div className="space-y-2">
                    {filteredCustomers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No customers found matching your search
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <Card
                          key={customer.id}
                          className={cn(
                            'cursor-pointer transition-all hover:border-primary',
                            selectedCustomer?.id === customer.id && 'border-primary bg-primary/5'
                          )}
                          onClick={() => handleSelectCustomer(customer)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">{customer.name}</span>
                                  {selectedCustomer?.id === customer.id && (
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  <span className="font-mono">{customer.accountNumber}</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                                  <div className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {customer.email}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {customer.phone}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {selectedCustomer && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Selected Customer Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-muted-foreground">Customer Name</Label>
                      <span className="font-medium">{selectedCustomer.name}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <Label className="text-muted-foreground">Account Number</Label>
                      <span className="font-mono font-medium">{selectedCustomer.accountNumber}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <Label className="text-muted-foreground">Email</Label>
                      <span className="font-medium">{selectedCustomer.email}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <Label className="text-muted-foreground">Phone</Label>
                      <span className="font-medium">{selectedCustomer.phone}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Promise Details Tab */}
            <TabsContent value="promise" className="px-6 py-4 space-y-6 mt-0 overflow-visible">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Promise Information</CardTitle>
                  <CardDescription>Enter the details of the payment commitment</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Promise Date */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Promise Date *
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !formData.promiseDate && 'text-muted-foreground'
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {formData.promiseDate ? format(formData.promiseDate, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={formData.promiseDate}
                          onSelect={(date) => setFormData({ ...formData, promiseDate: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Separator />

                  {/* Promised Amount */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Promised Amount *
                    </Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.promisedAmount}
                      onChange={(e) => setFormData({ ...formData, promisedAmount: e.target.value })}
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-muted-foreground">
                      This amount should match the total of invoice allocations
                    </p>
                  </div>

                  <Separator />

                  {/* Confirmation Method */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Confirmation Method *
                    </Label>
                    <Select
                      value={formData.confirmationMethod}
                      onValueChange={(value) => setFormData({ ...formData, confirmationMethod: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Phone">Phone Call</SelectItem>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Portal">Customer Portal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  {/* Related Collection Case (Optional) */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Folder className="h-4 w-4" />
                      Related Collection Case (Optional)
                    </Label>
                    <Select
                      value={formData.caseId || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, caseId: value === 'none' ? undefined : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a case (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {mockCases
                          .filter(c => c.status === 'Active')
                          .map((caseItem) => (
                            <SelectItem key={caseItem.id} value={caseItem.id}>
                              <div className="flex items-center gap-2">
                                <span className="font-mono">{caseItem.caseNumber}</span>
                                <Badge 
                                  variant={
                                    caseItem.priority === 'High' ? 'destructive' : 
                                    caseItem.priority === 'Medium' ? 'default' : 
                                    'secondary'
                                  }
                                  className="text-xs"
                                >
                                  {caseItem.priority}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Link this promise to a collection case for better tracking
                    </p>
                  </div>

                  <Separator />

                  {/* Send Confirmation */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="send-confirmation"
                      checked={formData.sendConfirmation}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, sendConfirmation: checked as boolean })
                      }
                    />
                    <Label htmlFor="send-confirmation" className="text-sm font-normal cursor-pointer">
                      Send confirmation email to customer
                    </Label>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Card */}
              {formData.promiseDate && formData.promisedAmount && (
                <Card className="border-primary/20 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="text-lg">Promise Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Customer</span>
                      <span className="font-medium">{selectedCustomer?.name}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Promise Date</span>
                      <span className="font-medium">{format(formData.promiseDate, 'MMM dd, yyyy')}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Promised Amount</span>
                      <span className="font-medium text-lg text-primary">
                        ${parseFloat(formData.promisedAmount).toLocaleString()}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Method</span>
                      <Badge variant="outline">{formData.confirmationMethod}</Badge>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices" className="px-6 py-4 space-y-6 mt-0 overflow-visible">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Select Invoices
                  </CardTitle>
                  <CardDescription>
                    Choose which invoices this promise applies to and allocate amounts
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Invoice Number</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-right">Days Overdue</TableHead>
                          <TableHead className="text-right">Allocated Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mockInvoices.map((invoice) => {
                          const isSelected = formData.selectedInvoices.has(invoice.id);
                          const allocatedAmount = formData.invoiceAllocations[invoice.id] || 0;

                          return (
                            <TableRow key={invoice.id}>
                              <TableCell>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) =>
                                    handleToggleInvoice(invoice.id, checked as boolean)
                                  }
                                />
                              </TableCell>
                              <TableCell className="font-medium font-mono">
                                {invoice.invoiceNumber}
                              </TableCell>
                              <TableCell>{format(invoice.invoiceDate, 'MMM dd, yyyy')}</TableCell>
                              <TableCell className="text-right">
                                ${invoice.balanceRemaining.toLocaleString()}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="destructive">{invoice.daysOverdue} days</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {isSelected ? (
                                  <Input
                                    type="number"
                                    value={allocatedAmount}
                                    onChange={(e) =>
                                      handleAllocationChange(invoice.id, parseFloat(e.target.value) || 0)
                                    }
                                    className="w-32 text-right"
                                    min="0"
                                    max={invoice.balanceRemaining}
                                    step="0.01"
                                  />
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Allocation Summary */}
              {formData.selectedInvoices.size > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Allocation Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Selected Invoices</span>
                      <span className="font-medium">{formData.selectedInvoices.size}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total Invoice Balance</span>
                      <span className="font-medium">${totalInvoiceAmount.toLocaleString()}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total Allocated</span>
                      <span className="font-medium text-lg">${totalAllocated.toLocaleString()}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Promised Amount</span>
                      <span
                        className={cn(
                          'font-medium text-lg',
                          formData.promisedAmount &&
                            Math.abs(totalAllocated - parseFloat(formData.promisedAmount)) < 0.01
                            ? 'text-green-600'
                            : 'text-amber-600'
                        )}
                      >
                        ${formData.promisedAmount ? parseFloat(formData.promisedAmount).toLocaleString() : '0.00'}
                      </span>
                    </div>
                    {formData.promisedAmount &&
                      Math.abs(totalAllocated - parseFloat(formData.promisedAmount)) > 0.01 && (
                        <>
                          <Separator />
                          <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                            <MessageSquare className="h-4 w-4" />
                            <span>
                              Allocations must equal promised amount. Difference: $
                              {Math.abs(totalAllocated - parseFloat(formData.promisedAmount)).toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Promise Notes
                  </CardTitle>
                  <CardDescription>Add any additional information about this promise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    placeholder="Enter notes about the promise, customer conversation, or any special conditions..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={8}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    These notes will be visible in the promise details and action history
                  </p>
                </CardContent>
              </Card>

              {/* Preview Card */}
              {formData.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Note Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">{formData.notes}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
